import { useRef, useEffect, useState } from "react";
import { ImagePair } from "@/data/galleryData";
import { useGalleryScroll } from "@/hooks/useGalleryScroll";
import { useIsMobile } from "@/hooks/use-mobile";
import { VideoPlayer } from "@/components/VideoPlayer";
import { getResponsiveProps } from "@/lib/responsiveImage";

interface SplitGalleryProps {
  pairs: ImagePair[];
  sectionTitle?: string;
  showPairTitle?: boolean;
  layout?: "vertical" | "horizontal";
}

export function SplitGallery({ pairs, sectionTitle, showPairTitle = false, layout = "vertical" }: SplitGalleryProps) {
  const { currentIndex, direction, isAnimating, navigate } = useGalleryScroll({
    totalPairs: pairs.length,
  });

  const isMobile = useIsMobile();
  const imageAlt = (pair?: ImagePair) => pair?.title || sectionTitle || "Obra do portfólio";
  const [displayIndex, setDisplayIndex] = useState(currentIndex);
  const [prevDisplayIndex, setPrevDisplayIndex] = useState(currentIndex);
  const [animPhase, setAnimPhase] = useState<"idle" | "ready" | "animating">("idle");
  const [fadeKey, setFadeKey] = useState(0);

  useEffect(() => {
    if (currentIndex !== displayIndex && !isAnimating) {
      setDisplayIndex(currentIndex);
      return;
    }
    if (currentIndex !== displayIndex && isAnimating) {
      setPrevDisplayIndex(displayIndex);
      setDisplayIndex(currentIndex);
      setAnimPhase("ready");
      setFadeKey((k) => k + 1);
    }
  }, [currentIndex, isAnimating]);

  useEffect(() => {
    if (animPhase === "ready") {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimPhase("animating");
        });
      });
    }
  }, [animPhase]);

  useEffect(() => {
    if (animPhase === "animating") {
      const timer = setTimeout(() => setAnimPhase("idle"), 900);
      return () => clearTimeout(timer);
    }
  }, [animPhase]);

  const currentPair = pairs[displayIndex];
  const prevPair = pairs[prevDisplayIndex];
  const pairTitle = showPairTitle ? currentPair?.title : sectionTitle;

  const isActive = animPhase === "ready" || animPhase === "animating";
  const isMoving = animPhase === "animating";

  const hasImage = (url: string | undefined) => Boolean(url && url.trim().length > 0);
  const isSingleImage = (pair: ImagePair | undefined) => {
    if (!pair) return false;
    const left = hasImage(pair.left);
    const right = hasImage(pair.right);
    if (left && right && pair.left === pair.right) return true;
    return (left && !right) || (!left && right);
  };

  const isHorizontalLayout = layout === "horizontal";

  const getTransforms = () => {
    if (isHorizontalLayout) {
      const exit = direction === "down" ? "-100vw" : "100vw";
      const enter = direction === "down" ? "100vw" : "-100vw";
      return { exitTop: exit, exitBottom: exit, enterTop: enter, enterBottom: enter };
    }
    if (isMobile) {
      const exitTop = direction === "down" ? "-100%" : "100%";
      const exitBottom = direction === "down" ? "100%" : "-100%";
      const enterTop = direction === "down" ? "100%" : "-100%";
      const enterBottom = direction === "down" ? "-100%" : "100%";
      return { exitTop, exitBottom, enterTop, enterBottom };
    } else {
      const exitLeft = direction === "down" ? "-100%" : "100%";
      const exitRight = direction === "down" ? "100%" : "-100%";
      const enterLeft = direction === "down" ? "100%" : "-100%";
      const enterRight = direction === "down" ? "-100%" : "100%";
      return { exitTop: exitLeft, exitBottom: exitRight, enterTop: enterLeft, enterBottom: enterRight };
    }
  };

  const transforms = getTransforms();
  const transitionStyle = "transform 0.9s cubic-bezier(0.76, 0, 0.24, 1)";
  const translateProp = isHorizontalLayout ? "translateX" : isMobile ? "translateX" : "translateY";

  const renderSingleImage = (
    pair: ImagePair,
    transformLeft: string,
    transformRight: string,
    transition: string,
    zIndex: number,
  ) => {
    const singleSrc = hasImage(pair.left) ? pair.left : pair.right;
    return (
      <>
        <div
          className="absolute top-0 left-0 w-full h-full overflow-hidden"
          style={{
            clipPath: isMobile ? "inset(0 0 50% 0)" : "inset(0 50% 0 0)",
            transition,
            transform: transformLeft,
            zIndex,
          }}
        >
          {hasImage(singleSrc) ? (
            <img
              {...getResponsiveProps(singleSrc, "100vw")}
              alt={imageAlt(pair)}
              className="w-full h-full object-cover"
              loading="eager"
            />
          ) : (
            <div className="w-full h-full bg-muted"></div>
          )}
        </div>
        <div
          className="absolute top-0 left-0 w-full h-full overflow-hidden"
          style={{
            clipPath: isMobile ? "inset(50% 0 0 0)" : "inset(0 0 0 50%)",
            transition,
            transform: transformRight,
            zIndex,
          }}
        >
          {hasImage(singleSrc) ? (
            <img
              {...getResponsiveProps(singleSrc, "100vw")}
              alt={imageAlt(pair)}
              className="w-full h-full object-cover"
              loading="eager"
            />
          ) : (
            <div className="w-full h-full bg-muted"></div>
          )}
        </div>
      </>
    );
  };

  const firstClass = isMobile ? "absolute top-0 left-0 w-full h-1/2" : "absolute top-0 left-0 w-1/2 h-full";
  const secondClass = isMobile ? "absolute bottom-0 left-0 w-full h-1/2" : "absolute top-0 right-0 w-1/2 h-full";

  const renderPairImage = (
    pair: ImagePair,
    transformFirst: string,
    transformSecond: string,
    transition: string,
    zIndex: number,
    loading: "eager" | "lazy" = "eager",
  ) => (
    <>
      <div className={firstClass} style={{ transition, transform: transformFirst, zIndex }}>
        {hasImage(pair.left) ? (
          <img
            {...getResponsiveProps(pair.left, isMobile ? "100vw" : "50vw")}
            alt={imageAlt(pair)}
            className="w-full h-full object-cover"
            loading={loading}
          />
        ) : (
          <div className="w-full h-full bg-muted"></div>
        )}
      </div>
      <div className={secondClass} style={{ transition, transform: transformSecond, zIndex }}>
        {hasImage(pair.right) ? (
          <img
            {...getResponsiveProps(pair.right, isMobile ? "100vw" : "50vw")}
            alt={imageAlt(pair)}
            className="w-full h-full object-cover"
            loading={loading}
          />
        ) : (
          <div className="w-full h-full bg-muted"></div>
        )}
      </div>
    </>
  );

  // Overlay que captura wheel sobre o iframe e repassa pro hook de scroll.
  // No hover, o z-index cai abaixo dos controles do VideoPlayer (z-10)
  // pra deixar o usuário interagir com play/pause/mute/seek.
  const VideoScrollOverlay = ({ navigate }: { navigate: (dir: "up" | "down") => void }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      const handler = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (Math.abs(e.deltaY) < 10) return;
        navigate(e.deltaY > 0 ? "down" : "up");
      };
      el.addEventListener("wheel", handler, { passive: false });
      return () => el.removeEventListener("wheel", handler);
    }, [navigate]);

    return (
      <div
        ref={ref}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 20,
          pointerEvents: hovered ? "none" : "auto",
          cursor: "default",
        }}
      ></div>
    );
  };

  const renderVideoSlide = (pair: ImagePair, transformFirst: string, transition: string, zIndex: number) => (
    <div className="absolute inset-0" style={{ transition, transform: transformFirst, zIndex }}>
      <VideoPlayer
        url={pair.video_url!}
        displayMode={(pair.video_display_mode as "viewport" | "background") || "viewport"}
        className="w-full h-full"
      />
      <VideoScrollOverlay navigate={navigate} />
    </div>
  );

  const renderSlide = (pair: ImagePair, role: "prev" | "current") => {
    const single = isSingleImage(pair);
    const isVideo = Boolean(pair.video_url);
    const zIndex = role === "prev" ? 2 : 1;
    const transition = isMoving ? transitionStyle : "none";

    if (role === "prev" && !isActive) return null;

    let transformFirst: string;
    let transformSecond: string;

    if (role === "prev") {
      transformFirst = isMoving ? `${translateProp}(${transforms.exitTop})` : `${translateProp}(0)`;
      transformSecond = isMoving ? `${translateProp}(${transforms.exitBottom})` : `${translateProp}(0)`;
    } else {
      transformFirst = isActive
        ? isMoving
          ? `${translateProp}(0)`
          : `${translateProp}(${transforms.enterTop})`
        : `${translateProp}(0)`;
      transformSecond = isActive
        ? isMoving
          ? `${translateProp}(0)`
          : `${translateProp}(${transforms.enterBottom})`
        : `${translateProp}(0)`;
    }

    if (isVideo) return renderVideoSlide(pair, transformFirst, transition, zIndex);
    if (single) return renderSingleImage(pair, transformFirst, transformSecond, transition, zIndex);
    return renderPairImage(
      pair,
      transformFirst,
      transformSecond,
      transition,
      zIndex,
      role === "prev" ? "lazy" : "eager",
    );
  };

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-background gallery-fade-wrapper">
      {isActive && prevPair && renderSlide(prevPair, "prev")}
      {currentPair && renderSlide(currentPair, "current")}
      {pairTitle && (
        <div
          key={fadeKey}
          className="fixed z-[50] gallery-title-fade"
          style={{
            bottom: 32,
            left: 32,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 16,
            fontWeight: 300,
            color: "hsl(var(--foreground))",
            backgroundColor: "hsl(var(--background))",
            padding: 8,
          }}
        >
          {pairTitle}
        </div>
      )}
    </div>
  );
}
